import React, { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Button } from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Autocomplete, Select, Input } from '~/components/Form';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function MyForm({
  close,
  restProps,
  rendering,
  toggleDisabled,
  ...rest
}) {
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const { client: idcliente } = useSelector((state) => state.user.profile);

  // Request in URL
  const handleRequest = useCallback(
    async (method, url, message, params = {}) => {
      try {
        await api[method](url, params);
        await close();

        toast.success(t(message));
      } catch (err) {
        err.map((e) => toast.error(e));
      }
    }
  );

  // submit form
  const handleSubmit = useCallback(async (data) => {
    toggleDisabled(true);
    setLoading(true);

    const { idempresaveiculo } = restProps;

    try {
      const schema = Yup.object().shape({
        idproduto: rest.idcliente
          ? Yup.string()
          : Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
        controlacotaempresaveiculocota: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        volumeempresaveiculocota: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0)),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      if (!rest.idcliente) {
        await handleRequest(
          'post',
          'empresasveiculoscotas',
          's.geral.sucessoinclusao',
          {
            ...data,
            idcliente,
            idempresaveiculo,
          }
        );
      } else {
        const { saldoempresaveiculocota, anomesempresaveiculocota } = rest;

        await handleRequest(
          'put',
          'empresasveiculoscotas',
          's.geral.sucessoalteracao',
          {
            ...data,
            idcliente,
            idempresaveiculo,
            saldoempresaveiculocota,
            anomesempresaveiculocota,
            volumeempresaveiculocota: Number(data.volumeempresaveiculocota),
          }
        );
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        formRef.current.setErrors(validationErrors);
      }
    } finally {
      toggleDisabled(false);
      setLoading(false);
    }
  }, []);

  const statusSimNao = [
    { label: t('s.geral.opcoes.nao'), value: 'N' },
    { label: t('s.geral.opcoes.sim'), value: 'S' },
  ];

  return (
    <S.Wrapper>
      <S.FormWrapper initialData={rest} onSubmit={handleSubmit} ref={formRef}>
        <Autocomplete
          name="idproduto"
          label="nomeproduto"
          url="produtos"
          unique="idproduto"
          title={t('s.menu.configuracao.produto')}
          disabled={!!rest.idcliente}
          excluded={!!rendering && rendering.map((r) => r.idproduto)}
        />

        <Select
          name="controlacotaempresaveiculocota"
          label={t('s.cadastro.empresaveiculo.cota')}
          options={statusSimNao}
        />

        <Input
          name="volumeempresaveiculocota"
          label={t('s.cadastro.empresaveiculo.volumeempresaveiculocota')}
          type="number"
          min={0}
        />
      </S.FormWrapper>

      <S.GroupButtons>
        <Button
          startIcon={<Check />}
          color="primary"
          variant="outlined"
          onClick={() => formRef.current.submitForm()}
          disabled={loading}
        >
          {t('s.geral.botaoconfirmar')}
        </Button>

        <Button
          startIcon={<Close />}
          color="secondary"
          variant="outlined"
          onClick={() => close()}
          disabled={loading}
        >
          {t('s.geral.botaocancelar')}
        </Button>
      </S.GroupButtons>
    </S.Wrapper>
  );
}

MyForm.propTypes = {
  restProps: PropTypes.objectOf(PropTypes.any).isRequired,
  close: PropTypes.func.isRequired,
  rendering: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleDisabled: PropTypes.func.isRequired,
};
